import { useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, alpha, useTheme
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { SectionPaper, SectionTitle } from './styledComponents';

const PreparedBySection = ({ invoice, onUpdate, canEdit = true }) => {
  const theme = useTheme();
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState({ name: '', position: '' });

  const handleOpenDialog = () => {
    if (!canEdit) return;
    setSignaturePreview({
      name: invoice.prepared_by?.name || '',
      position: invoice.prepared_by?.position || 'Sales Representative'
    });
    setSignatureDialogOpen(true);
  };

  const handleSaveSignature = () => {
    if (!signaturePreview.name.trim()) return;
    onUpdate({
      prepared_by: {
        name: signaturePreview.name.trim(),
        position: signaturePreview.position.trim() || 'Sales Representative',
        date: new Date().toISOString(),
        typed: true
      }
    });
    setSignatureDialogOpen(false);
  };

  return (
    <>
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6"><EditIcon />Prepared By</SectionTitle>

        <Box sx={{
          p: 3, borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.02)
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {invoice.prepared_by ? (
              <Box sx={{ flex: 1, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                <Box sx={{ fontFamily: "'Brush Script MT', cursive", fontSize: '24px', mb: 1 }}>
                  {invoice.prepared_by.name}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {invoice.prepared_by.position || 'Sales Representative'}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                No signature provided
              </Typography>
            )}
            <Button variant="outlined" size="small" onClick={handleOpenDialog}
              sx={{
                ml: 2, opacity: canEdit ? 1 : 0.3,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}>
              {invoice.prepared_by ? 'Change' : 'Add'}
            </Button>
          </Box>
        </Box>
      </SectionPaper>

      <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)} maxWidth="sm" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>Add Prepared By Signature</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField label="Name" fullWidth margin="normal"
            value={signaturePreview.name}
            onChange={(e) => setSignaturePreview(prev => ({ ...prev, name: e.target.value }))} />
          <TextField label="Position/Title" fullWidth margin="normal"
            value={signaturePreview.position}
            onChange={(e) => setSignaturePreview(prev => ({ ...prev, position: e.target.value }))} />
          <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
            <Typography variant="body2" gutterBottom>Preview:</Typography>
            <Box sx={{ fontFamily: "'Brush Script MT', cursive", fontSize: '24px', minHeight: '32px' }}>
              {signaturePreview.name || 'Your name will appear here'}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {signaturePreview.position || 'Sales Representative'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSignatureDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSignature} disabled={!signaturePreview.name.trim()}>
            Save Signature
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PreparedBySection;
